import React, { useState, useEffect } from "react";
import {
  useTheme,
  useMediaQuery,
  Card,
  Grid,
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  TablePagination,
  Stack,
  Autocomplete,
  Popover,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { usePermission } from "../hooks/usePermission";
import config from "../config/config";
import { useToast } from "../context/ToastContext";
import AddOrderDialog from "../components/AddOrderDialog";
import InvoiceModal from "../components/InvoiceModal";
import { formatNumber, parseNumber } from "../utils/helpers";
import { ConfirmDialog } from "../components/custom-dialog";
import { useBoolean } from "../hooks/use-boolean";
import ProductPreview from "../components/ProductPreview";
import imageCompression from "browser-image-compression";

const Products = () => {
  const { showToast } = useToast();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalInventory, setTotalInventory] = useState(0);
  const [open, setOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [form, setForm] = useState({
    name: "",
    sku: "",
    description: "",
    price: "",
    quantity: "",
    productImage: null,
    category: "",
  });
  const [productImagePreview, setProductImagePreview] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const { token } = useAuth();
  const { hasPermission } = usePermission();

  const [isAddOrderDialogOpen, setIsAddOrderDialogOpen] = useState(false);
  const [productForOrder, setProductForOrder] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [viewMode, setViewMode] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Thêm state cho Popover
  const [anchorEl, setAnchorEl] = useState(null);

  // Mở popover
  const handleOpenFilters = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Đóng popover
  const handleCloseFilters = () => {
    setAnchorEl(null);
  };

  const openPopover = Boolean(anchorEl);
  const id = openPopover ? "mobile-filter-popover" : undefined;

  const confirm = useBoolean();

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchDashboardStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterCategory, isMobile]);

  const fetchDashboardStats = async () => {
    try {
      const res = await axios.get(`${config.backendUrl}/api/dashboard/stats`, {
        headers: { "x-auth-token": token },
      });
      setTotalInventory(res.data.totalInventory || 0);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProducts = async (append = false, pageOverride = null) => {
    setIsLoadingMore(true);
    try {
      const params = {
        page: pageOverride !== null ? pageOverride : (isMobile ? page + 1 : page + 1),
        limit: rowsPerPage,
      };
      if (searchQuery) params.search = searchQuery;
      if (filterCategory) params.category = filterCategory;

      const res = await axios.get(`${config.backendUrl}/api/products`, {
        headers: { "x-auth-token": token },
        params,
      });

      const newProducts = res.data.docs || [];
      const total = res.data.totalDocs || 0;

      if (append) {
        setProducts((prev) => [...prev, ...newProducts]);
      } else {
        setProducts(newProducts);
      }

      setTotalProducts(total);
      const currentPage = pageOverride !== null ? pageOverride : (isMobile ? page + 1 : page + 1);
      setHasMore(currentPage * rowsPerPage < total);
    } catch (err) {
      console.error(err);
      if (!append) {
        setProducts([]);
        setTotalProducts(0);
      }
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${config.backendUrl}/api/categories`, {
        headers: { "x-auth-token": token },
      });
      setCategories(res.data.docs || []); // Ensure categories is always an array
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]); // Set to empty array on error to prevent issues
    }
  };

  const handleClickOpen = (product = null) => {
    setCurrentProduct(product);
    if (product) {
      setForm({
        _id:product._id,
        name: product.name,
        sku: product.sku,
        description: product.description,
        price: product.price,
        quantity: product.quantity,
        productImage: null,
        category: product.category ? product.category._id : "",
        costPrice: product.costPrice || 0,
      });
      setProductImagePreview(
        product.imageUrl ? `${config.backendUrl}${product.imageUrl}` : null
      );
    } else {
      setForm({
        name: "",
        sku: "",
        description: "",
        price: "",
        quantity: "",
        productImage: null,
        category: "",
        costPrice: "",
      });
      setProductImagePreview(null);
      setViewMode(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentProduct(null);
    setForm({
      name: "",
      sku: "",
      description: "",
      price: "",
      quantity: "",
      productImage: null,
      category: "",
      costPrice: "",
    });
    setProductImagePreview(null);
    setValidationErrors({});
    setViewMode(true);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file exceeds 10MB
      if (file.size > 10 * 1024 * 1024) {
        showToast("Kích thước ảnh không được vượt quá 10MB.", "error");
        e.target.value = null;
        return;
      }
      
      // Compress images larger than 5MB
      if (file.size > 5 * 1024 * 1024) {
        try {
          const options = {
            maxSizeMB: 5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          };
          const compressedFile = await imageCompression(file, options);
          setForm({ ...form, productImage: compressedFile });
          setProductImagePreview(URL.createObjectURL(compressedFile));
          showToast("Ảnh đã được nén thành công.", "success");
        } catch (error) {
          showToast("Lỗi khi nén ảnh.", "error");
          e.target.value = null;
          setForm({ ...form, productImage: null });
          setProductImagePreview(null);
        }
      } else {
        setForm({ ...form, productImage: file });
        setProductImagePreview(URL.createObjectURL(file));
      }
    } else {
      setForm({ ...form, productImage: null });
      setProductImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!form.name) errors.name = "Tên sản phẩm là bắt buộc";
    if (!form.price || form.price <= 0)
      errors.price = "Giá phải là một số dương";
    if (!form.costPrice || form.costPrice <= 0)
      errors.costPrice = "Giá vốn phải là một số dương";
    if (!form.quantity || form.quantity <= 0)
      errors.quantity = "Số lượng phải là một số dương";

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    const formData = new FormData();
    for (const key in form) {
      if (key === "productImage" && !form[key]) continue; // Don't append if no new image
      formData.append(key, form[key]);
    }

    try {
      if (currentProduct) {
        await axios.put(
          `${config.backendUrl}/api/products/${currentProduct._id}`,
          formData,
          {
            headers: {
              "x-auth-token": token,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        showToast("Sản phẩm đã được cập nhật thành công!", "success");
      } else {
        await axios.post(`${config.backendUrl}/api/products`, formData, {
          headers: {
            "x-auth-token": token,
            "Content-Type": "multipart/form-data",
          },
        });
        showToast("Sản phẩm đã được thêm thành công!", "success");
      }
      fetchProducts();
      handleClose();
      setValidationErrors({}); // Clear errors on success
      if (productImagePreview) {
        URL.revokeObjectURL(productImagePreview);
      }
    } catch (err) {
      console.error(err);
      console.log("tesst loi")
      let errorMessage = "Đã xảy ra lỗi khi lưu sản phẩm.";
      if (err.response && err.response.data) {
        if (err.response.data.errors) {
          const apiErrors = {};
          err.response.data.errors.forEach((error) => {
            apiErrors[error.param] = error.msg;
          });
          setValidationErrors(apiErrors);
          errorMessage = err.response.data.errors[0].msg || errorMessage;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }
      showToast(errorMessage, "error");
    }
  };

  const handleOpenAddOrderDialog = (product) => {
    setProductForOrder(product);
    setIsAddOrderDialogOpen(true);
  };

  const handleCloseAddOrderDialog = () => {
    setIsAddOrderDialogOpen(false);
    setProductForOrder(null);
  };

  const handleOrderAdded = (order) => {
    fetchProducts(); // Refetch products to update stock
    const foundItemPro = products.find(
      (item) => item._id.toString() === order.items[0].product._id.toString()
    );
    const orderInfo = {
      ...order,
      orderCode: order.orderCode || order._id, // Ensure orderCode is set
      items: order.items.map((item) => ({
        ...item,
        product: foundItemPro,
      })),
      debt: order.customer.debt + (order.discountPrice - order.amountPaid), // Total  debt of the customer
      debt_old: order.customer.debt, // Current
    };
    setSelectedOrder(orderInfo); // Lưu thông tin đơn hàng vừa tạo
    setShowInvoiceModal(true); // Mở InvoiceModal
  };

  const handleDelete = async (id) => {
    confirm.onFalse(); // Close the confirmation dialog
    try {
      await axios.delete(`${config.backendUrl}/api/products/${id}`, {
        headers: { "x-auth-token": token },
      });
      fetchProducts();
      showToast("Sản phẩm đã được xóa thành công!", "success");
      handleClose();
    } catch (err) {
      console.error(err);
      let errorMessage = "Đã xảy ra lỗi khi xóa sản phẩm.";
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      }
      showToast(errorMessage, "error");
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ px: { xs: -20, sm: 3, md: 4 }, py: 3 }}>
        <Typography
          variant={isMobile ? "h6" : "h4"}
          gutterBottom
          sx={{ fontWeight: 700, mb: 4, mt: isMobile ? -4 : 4 }}
        >
          Quản lý sản phẩm
        </Typography>
        <Typography
          variant={isMobile ? "h7" : "h5"}
          component="h4"
          gutterBottom
          sx={{ textAlign: "left" }}
        >
          <b>Tổng tồn kho: </b>
          {totalInventory}
        </Typography>
        {totalProducts} Sản phẩm
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            mb: 2,
            position: isMobile ? "sticky" : "static",
            top: isMobile ? "56px" : "auto",
            zIndex: 10,
            backgroundColor: "background.paper",
            p: 0,
            borderBottom: isMobile ? "1px solid #ddd" : "none",
          }}
        >
          <TextField
            label="Tìm kiếm theo tên hoặc SKU"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
          />
          {isMobile ? (
            <>
              {" "}
              <Box sx={{ alignSelf: "flex-start" }}>
                <Button variant="text" onClick={handleOpenFilters}>
                  Lọc thêm
                </Button>
              </Box>
              <Popover
                id={id}
                open={openPopover}
                anchorEl={anchorEl}
                onClose={handleCloseFilters}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    minWidth: 250,
                    maxWidth: 300,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Autocomplete
                    fullWidth
                    options={[{ _id: "", name: "Tất cả" }, ...categories]}
                    getOptionLabel={(option) => option.name}
                    value={
                      categories.find((cat) => cat._id === filterCategory) || {
                        _id: "",
                        name: "Tất cả",
                      }
                    }
                    onChange={(event, newValue) => {
                      setFilterCategory(newValue ? newValue._id : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Danh mục"
                        variant="outlined"
                      />
                    )}
                  />
                </Box>{" "}
              </Popover>
            </>
          ) : (
            <Autocomplete
              fullWidth
              options={[{ _id: "", name: "Tất cả" }, ...categories]}
              getOptionLabel={(option) => option.name}
              value={
                categories.find((cat) => cat._id === filterCategory) || {
                  _id: "",
                  name: "Tất cả",
                }
              }
              onChange={(event, newValue) => {
                setFilterCategory(newValue ? newValue._id : "");
              }}
              renderInput={(params) => (
                <TextField {...params} label="Danh mục" variant="outlined" />
              )}
            />
          )}
        </Box>
        {hasPermission(["product:create"]) && (
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: isMobile ? "center" : "flex-start",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              fullWidth={isMobile}
              onClick={() => handleClickOpen()}
            >
              Thêm sản phẩm
            </Button>
          </Box>
        )}
        {isMobile ? (
          // <Grid container spacing={2} sx={{ mt: 2 }}>
          <Stack spacing={1.5}>
            {products.length > 0 ? (
              products.map((product) => (
                // <Grid item xs={12} key={product._id}>
                <Card
                  key={product._id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    p: 1.5,
                    borderRadius: 2,
                    boxShadow: 1,
                    minHeight: 90,
                    cursor: "pointer",
                  }}
                  onClick={() => handleClickOpen(product)}
                >
                  {/* Ảnh sản phẩm */}
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      flexShrink: 0,
                      borderRadius: 1,
                      overflow: "hidden",
                      mr: 2,
                      bgcolor: "#f5f5f5",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {product.imageUrl && (
                      <img
                        src={`${config.backendUrl}${product.imageUrl}`}
                        alt={product.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </Box>

                  {/* Nội dung sản phẩm */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      overflow: "hidden",
                      minWidth: 0, // để text ellipsis hoạt động
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={500}
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: "1.4em",
                        maxHeight: "2.8em",
                      }}
                    >
                      {product.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {Number(product.price).toLocaleString()}₫ — Tồn:{" "}
                      {product.quantity}
                    </Typography>
                  </Box>

                  {/* Nút đặt hàng */}
                  {hasPermission(["order:create"]) && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenAddOrderDialog(product);
                      }}
                      color="success"
                      sx={{
                        ml: 2,
                        flexShrink: 0,
                        alignSelf: "center",
                      }}
                    >
                      <AddShoppingCartIcon />
                    </IconButton>
                  )}
                </Card>
                // </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography
                  align="center"
                  variant="body2"
                  color="text.secondary"
                >
                  Không có sản phẩm nào.
                </Typography>
              </Grid>
            )}
            {isMobile && hasMore && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const nextPage = page + 1;
                    setPage(nextPage);
                    fetchProducts(true, nextPage + 1); // append = true, pageOverride = nextPage + 1
                  }}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? "Đang tải..." : "Tải thêm sản phẩm"}
                </Button>
              </Box>
            )}
          </Stack>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ mt: 4 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Hình ảnh</TableCell>
                    <TableCell>Tên</TableCell>
                    <TableCell>Mã SKU</TableCell>
                    <TableCell>Mô tả</TableCell>
                    <TableCell>Giá</TableCell>
                    <TableCell>Số lượng</TableCell>
                    <TableCell>Danh mục</TableCell>
                    <TableCell>Hành động</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products && products.length > 0 ? (
                    products.map((product) => (
                      <TableRow key={product._id}>
                        <TableCell>
                          {product.imageUrl && (
                            <img
                              src={`${config.backendUrl}${product.imageUrl}`}
                              alt={product.name}
                              style={{
                                width: 50,
                                height: 50,
                                objectFit: "cover",
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>{product.sku}</TableCell>
                        <TableCell>{product.description}</TableCell>
                        <TableCell>
                          {Number(product.price).toLocaleString()}₫
                        </TableCell>
                        <TableCell>{product.quantity}</TableCell>
                        <TableCell>
                          {product.category ? product.category.name : "N/A"}
                        </TableCell>
                        <TableCell>
                          {hasPermission(["product:update"]) && (
                            <IconButton
                              onClick={() => handleClickOpen(product)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {hasPermission(["product:delete"]) && (
                            <IconButton
                              onClick={() => handleDelete(product._id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                          {hasPermission(["order:create"]) && (
                            <IconButton
                              onClick={() => handleOpenAddOrderDialog(product)}
                              color="success"
                              disabled={product.quantity === 0}
                            >
                              <AddShoppingCartIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        Không có sản phẩm nào.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalProducts}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
        <Dialog
          open={open}
          fullScreen={isMobile}
          minWidth="lg"
          onClose={(event, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              pt: 2,
            }}
          >
            <DialogTitle sx={{ m: 0, p: 0 }}>
              {currentProduct
                ? viewMode
                  ? "Chi tiết"
                  : "Chỉnh sửa sản phẩm"
                : "Thêm sản phẩm"}
            </DialogTitle>

            <Stack direction="row" spacing={2}>
              {hasPermission(["product:delete"]) &&
                currentProduct &&
                isMobile && (
                  <IconButton
                    onClick={() => confirm.onTrue()}
                    color="error"
                    aria-label="Xóa"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              <IconButton
                onClick={() => setViewMode((v) => !v)}
                color={viewMode ? "primary" : "warning"}
                aria-label={viewMode ? "Cập nhật" : "Xem sản phẩm"}
              >
                {viewMode ? <EditIcon /> : <RemoveRedEyeIcon />}
              </IconButton>
            </Stack>
          </Box>
          <DialogContent>
            {viewMode ? (
              <ProductPreview
                form={form}
                productImagePreview={productImagePreview}
                categoryName={
                  categories.find((c) => c._id === form.category)?.name ||
                  "Chưa chọn"
                }
              />
            ) : (
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Tên Sản phẩm"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  error={!!validationErrors.name}
                  helperText={validationErrors.name}
                />
                <TextField
                  margin="normal"
                  fullWidth
                  label="SKU"
                  name="sku"
                  value={form.sku}
                  onChange={handleChange}
                  error={!!validationErrors.sku}
                  helperText={validationErrors.sku}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label={currentProduct ? "Tồn kho" : "Số lượng"}
                  name="quantity"
                  type="number"
                  value={form.quantity}
                  onChange={handleChange}
                  error={!!validationErrors.quantity}
                  helperText={validationErrors.quantity}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Giá bán"
                  name="price"
                  value={form.price === 0 ? "" : formatNumber(form.price)}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm({
                      ...form,
                      price: val === "" ? "" : parseNumber(val),
                    });
                  }}
                  error={!!validationErrors.price}
                  helperText={validationErrors.price}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Giá vốn"
                  name="costPrice"
                  value={
                    form.costPrice === 0 ? "" : formatNumber(form.costPrice)
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm({
                      ...form,
                      costPrice: val === "" ? "" : parseNumber(val),
                    });
                  }}
                  error={!!validationErrors.costPrice}
                  helperText={validationErrors.costPrice}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  margin="normal"
                  fullWidth
                  label="Mô tả"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  error={!!validationErrors.description}
                  helperText={validationErrors.description}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel id="category-label">Danh mục</InputLabel>
                  <Select
                    labelId="category-label"
                    id="category"
                    name="category"
                    value={form.category}
                    label="Danh mục"
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ mt: 2, mb: 2 }}>
                  {productImagePreview && (
                    <img
                      src={productImagePreview}
                      alt="Product Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 200,
                        objectFit: "contain",
                      }}
                    />
                  )}
                </Box>
                <Button variant="contained" component="label" sx={{ mt: 2 }}>
                  Tải ảnh lên
                  <input
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    name="productImage"
                    accept="image/*"
                  />
                </Button>
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>
              {viewMode && currentProduct ? "Đóng" : "Hủy"}
            </Button>
            {!viewMode && (
              <Button onClick={handleSubmit}>
                {currentProduct ? "Cập nhật" : "Thêm"}
              </Button>
            )}
          </DialogActions>
        </Dialog>
        <ConfirmDialog
          open={confirm.value}
          onClose={confirm.onFalse}
          title="Xóa sản phẩm"
          content="Bạn có muốn xóa sản phẩm này ?"
          action={
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleDelete(currentProduct._id)}
            >
              Đồng ý
            </Button>
          }
        />
        <AddOrderDialog
          open={isAddOrderDialogOpen}
          onClose={handleCloseAddOrderDialog}
          product={productForOrder}
          onOrderAdded={handleOrderAdded}
        />
        <InvoiceModal
          show={showInvoiceModal}
          handleClose={() => setShowInvoiceModal(false)}
          order={selectedOrder} // Truyền dữ liệu đơn hàng vào InvoiceModal
        />
      </Box>
    </Container>
  );
};

export default Products;